import * as Sentry from "@sentry/react";
import {
  PerformanceMonitor,
  WebVitalsTracker,
  MetricsCollector,
  DEFAULT_PERFORMANCE_MONITOR_CONFIG,
  DEFAULT_WEB_VITALS_CONFIG,
  DEFAULT_METRICS_CONFIG,
  type WebVitalMetric,
  type CustomMetric,
} from "@/packages/performance-monitoring";

let performanceMonitor: PerformanceMonitor | null = null;

/**
 * Initialize performance monitoring for the web application.
 * Integrates with Sentry for error tracking and performance reporting.
 */
export function initPerformanceMonitoring(): void {
  if (performanceMonitor) {
    return;
  }

  const isProduction = import.meta.env.PROD;

  // Configure Web Vitals tracking with Sentry integration
  const webVitalsConfig = {
    ...DEFAULT_WEB_VITALS_CONFIG,
    sampleRate: isProduction ? 0.25 : 1.0, // Sample 25% in production
    onMetric: (metric: WebVitalMetric) => {
      // Report to Sentry
      Sentry.setMeasurement(metric.name, metric.value, getUnit(metric.name));

      // Log warning for poor metrics
      if (metric.rating === "poor") {
        Sentry.captureMessage(`Poor Web Vital: ${metric.name}`, {
          level: "warning",
          extra: {
            value: metric.value,
            rating: metric.rating,
            page: metric.page,
          },
        });
      }
    },
    onError: (error: Error) => {
      console.error("Web Vitals error:", error);
    },
  };

  // Configure metrics collection
  const metricsConfig = {
    ...DEFAULT_METRICS_CONFIG,
    onMetric: (metric: CustomMetric) => {
      // Report custom metrics to Sentry
      if (
        metric.name.startsWith("navigation.") ||
        metric.name.startsWith("resource.")
      ) {
        Sentry.setMeasurement(metric.name, metric.value, "millisecond");
      }
    },
  };

  // Configure performance monitor with threshold alerting
  const monitorConfig = {
    ...DEFAULT_PERFORMANCE_MONITOR_CONFIG,
    enabled: true,
    sampleRate: isProduction ? 0.25 : 1.0,
    onThresholdExceeded: (name: string, value: number, threshold: number) => {
      Sentry.captureMessage(`Performance threshold exceeded: ${name}`, {
        level: "warning",
        extra: {
          metricName: name,
          value,
          threshold,
          exceededBy: value - threshold,
        },
      });
    },
  };

  // Create trackers and collectors
  const webVitalsTracker = new WebVitalsTracker(webVitalsConfig);
  const metricsCollector = new MetricsCollector(metricsConfig);

  // Create the performance monitor
  performanceMonitor = new PerformanceMonitor(
    monitorConfig,
    metricsCollector,
    webVitalsTracker,
  );

  // Initialize asynchronously
  performanceMonitor.initialize().catch((error: Error) => {
    console.error("Failed to initialize performance monitoring:", error);
  });
}

/**
 * Get the performance monitor instance.
 */
export function getPerformanceMonitor(): PerformanceMonitor | null {
  return performanceMonitor;
}

/**
 * Get the appropriate unit for Sentry measurement based on metric name.
 */
function getUnit(name: string): "millisecond" | "none" {
  switch (name) {
    case "CLS":
      return "none"; // CLS is a score, not time
    case "INP":
    case "FCP":
    case "LCP":
    case "TTFB":
      return "millisecond";
    default:
      return "none";
  }
}
